<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <SModal
    v-bind="$attrs"
    v-model="displayModal"
    :header-class="isAuthor && isPreviewMode ? 'pt-2 pr-2 pl-2 pb-0 d-flex flex-column' : 'p-2'"
    :hide-footer="isPreviewMode"
    :show-footer-gradient="false"
    :return-focus="returnFocusToElement"
    :refocus-first-element="survey.is_user_voted"
    :header-title="$t('survey.voting.title')"
    :header-sub-title="$t('survey.number_of_questions_survey_subtitle', { numOfQuestions: questionsLength })"
    @header-left-action="$emit('close')"
    @hide="closeAndUpdateSurveyModal"
    v-on="$listeners"
  >
    <template #header-additional v-if="isAuthor && isPreviewMode">
      <s-tab-header
        ref="tabHeader"
        class="pt-4"
        :tabs="[
          { id: 'overview', text: $t('survey.preview.tabs.overview') },
          { id: 'answers', text: $t('survey.preview.tabs.answers') },
          { id: 'open', text: $t('survey.preview.tabs.open') },
        ]"
        :save-active-tab-in-route="false"
        data-test="survey-preview-tabs"
        @change="(tab) => (activeTab = tab)"
      />
    </template>

    <div v-if="showExport" class="d-flex justify-content-end align-items-center">
      <SDropDown variant="link"
                 right
                 toggle-class="text-decoration-none"
                 no-caret
                 data-test="survey-export-dropdown"
      >
        <template #button-content>
          <span class="d-flex btn btn-link align-items-center p-0 with-icon no-border">
            {{ $t('survey.voters_list.export.title') }}
            <sdui-icon class="ml-2 icon-chevron-down" />
          </span>
        </template>

        <SLink :href="survey.xlsx"
               appearance="b-dropdown-item"
               download
        >
          <sdui-icon class="icon-download-o d-inline-block align-middle text-primary" />
          <span class="mx-2" aria-hidden="true">
            {{ $t('survey.voters_list.export.excel') }} {{ `(ZIP, ${$getSize(xlsFileSize, false)})` }}</span>
          <span class="sr-only">{{
            `${$t('survey.voters_list.export.export_as', { filetype: 'XLS' })} (${$getSize(xlsFileSize, false)})`
          }}</span>
        </SLink>
      </SDropDown>
    </div>

    <template v-if="displayModal">
      <sdui-multi-question-survey-options-overview
        v-if="activeTab.id === 'overview'"
        :survey="survey"
        :input-prefix="inputPrefix"
        :is-author="isAuthor"
        :is-preview-mode="isPreviewMode"
        :show-results="showResults"
        @close-and-update="closeAndUpdateOverViewModal"
      />

      <anonymous-survey-tab-content v-else-if="survey.is_anonymous" @change-active-tab="changeTab" />

      <sdui-multi-question-survey-voters-list v-else-if="activeTab.id === 'answers'" :survey="survey" />

      <sdui-multi-question-survey-voters-list-open v-else :survey="survey" />
    </template>
  </SModal>
</template>

<script>
import SurveyMixin from 'src/mixins/channels/survey/surveyMixin.js'
import STabHeader from 'src/components/base/STabHeader.vue'
import SduiMultiQuestionSurveyOptionsOverview from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurveyOptionsOverview.vue'
import SduiMultiQuestionSurveyVotersList from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurveyVotersList.vue'
import SduiMultiQuestionSurveyVotersListOpen from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurveyVotersListOpen.vue'
import AnonymousSurveyTabContent from 'src/components/miscellaneous/ui/surveys/AnonymousSurveyTabContent.vue'
import Misc from 'src/api/misc.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SduiMultiQuestionSurveyOptionsModal',
  components: {
    AnonymousSurveyTabContent,
    SduiMultiQuestionSurveyVotersList,
    SduiMultiQuestionSurveyVotersListOpen,
    SduiMultiQuestionSurveyOptionsOverview,
    STabHeader,
  },

  mixins: [SurveyMixin],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    survey: {
      type: Object,
      required: true,
    },

    isPreviewMode: {
      type: Boolean,
      default: false,
    },

    showResults: {
      type: Boolean,
      default: false,
    },

    inputPrefix: {
      type: String,
      default: 'option',
    },

    isAuthor: {
      type: Boolean,
      default: false,
    },

    isDisplayedInNews: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    displayModal: false,
    activeTab: { id: 'overview' },
    xlsFileSize: 0,
    voteSubmited: false,
  }),

  computed: {
    showExport() {
      return this.isAuthor && this.isPreviewMode && this.survey.xlsx && !(this.activeTab.id !== 'overview' && this.survey.is_anonymous)
    },

    questionsLength() {
      return this.survey.questions.length
    },

    returnFocusToElement() {
      if (this.isDisplayedInNews) return `#sdui-news-dropdown-${this.$route.query.reference}__BV_toggle_`
      return `#options-modal-${this.$route.query.reference}__BV_toggle_`
    },
  },

  emits: ['close', 'closeAndUpdate'],

  methods: {
    changeTab(event, tab = 'overview') {
      this.$refs.tabHeader.setActiveTabId({ id: tab })
    },

    async initFileSizes() {
      if (!this.survey.xlsx) return
      const resXls = await Misc.headDownloadableFile(this.survey.xlsx)
      this.xlsFileSize = resXls?.headers?.['content-size'] || 0
    },

    async closeAndUpdateSurveyModal() {
      await this.$nextTick()
      const surveyParticipatedEl = document.querySelector(`#survey-paricipated-${this.survey.id}`)
      const previewModalBtn = document.querySelector(`#preview-modal-btn-${this.survey.id}`)

      if (surveyParticipatedEl && this.survey.is_user_voted && this.voteSubmited) surveyParticipatedEl.focus()
      if (previewModalBtn && this.isPreviewMode && !this.voteSubmited) previewModalBtn.focus()

      this.voteSubmited = false
    },

    closeAndUpdateOverViewModal(data) {
      this.voteSubmited = true
      this.$emit('closeAndUpdate', data)
    },
  },

  watch: {
    modelValue: {
      async handler(displayModal) {
        this.displayModal = displayModal
        if (!this.displayModal) return
        await this.initFileSizes()
        this.activeTab = { id: 'overview' }
      },

      immediate: true,
    },
  },
}
</script>
